import React, { useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { selectIsAuthLoading, setAuthError, setAuthLoading, setUser } from "@shared/store/auth.duck";
import { closeDialog } from "@shared/store/dialog.duck";
import { setLoading } from '../../store/registration.duck';
import { agent } from '@shared/services/agent';
import { Auth } from "@shared/services/Auth";
import { TrackJS } from "trackjs";
import { isInTeams } from "@shared/utils/TeamsUtils";
import { PrimaryButton } from '@shared/ui/atoms/buttons/PrimaryButton';
import { emitRegisterEvent } from '@features/auth/services/RegisterService';
import { registerWithMicrosoft, setLifecycle } from '@shared/auth/authMachine.duck';
import { BodyError } from '@shared/ui/atoms/fonts/Body';
import { classifyAuthError } from "@shared/utils/error-helper";
import { getNotificationMessage, NotificationMessages } from "@shared/models/notification.models";
import { RequestAdminConsentButton } from "./RequestAdminConsentButton";
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';

export function RegisterWithMicrosoftTeamsButton(_props: Props) {
  const dispatch = useDispatch();
  const isLoading = useSelector(selectIsAuthLoading);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<NotificationMessages | null>(null);

  const registerWithServer = async (): Promise<boolean> => {
    const informMe = localStorage.getItem('informMe') === 'true';
    try {
      await agent.requests.post('/register/azure', {informMe: informMe, inTeams: isInTeams()});
      const user = await Auth.current(false);
      dispatch(setUser(user));
      // Registration is complete and the session tokens acquired by
      // registerWithMicrosoft are live, so the machine is sessionReady. Without
      // this the lifecycle stays 'registering' for the whole SPA session (Teams
      // never reloads the tab, and WithTeamsAuth skips bootstrapAuth once a
      // user is set), which pins the calendar-sync indicator on Pending and
      // reverts every calendar state change via enforceInvariants.
      dispatch(setLifecycle('sessionReady'));
      dispatch(setAuthLoading(false));
      return true;
    } catch (e: any) {
      console.error(e);
      const message = (e?.message ?? '').toString().trim() || 'Failed to register';
      dispatch(setAuthError(message));
      setErrorMessage(message);
      TrackJS?.track(e);
      return false;
    }
  }

  const register = async () => {
    if (isLoading) return;
    setErrorMessage(null);
    setErrorCode(null);

    try {
      dispatch(setLoading(true));

      emitRegisterEvent();

      await (dispatch(registerWithMicrosoft({ source: 'teams' })) as any).unwrap();
      const ok = await registerWithServer();

      // Only close the terms dialog if registration fully succeeded — otherwise leave it
      // open so the inline error stays visible to the user.
      if (ok) {
        dispatch(closeDialog());
      }
    } catch (e: any) {
      const code = classifyAuthError(e);
      setErrorCode(code);
      if (code === NotificationMessages.unknownError) {
        TrackJS?.track(e);
        console.log(e);
        const message = (e?.message ?? '').toString().trim() || 'Failed to register';
        setErrorMessage(message);
      } else {
        TrackJS?.track(`[RegisterWithMicrosoftTeamsButton] auth failed: code=${code}, errorCode=${e?.errorCode}, message=${(e?.message ?? '').slice(0, 200)}`);
        setErrorMessage(getNotificationMessage(code) ?? 'Failed to register');
      }
    } finally {
      dispatch(setLoading(false))
    }
  }
  return (
    <div>
      <PrimaryButton click={register} fullWidth={true} size={ButtonSize.Large} text="button.continue-to-signup"/>
      {errorMessage && <BodyError style={{marginTop: 12}}>{errorMessage}</BodyError>}
      {errorCode === NotificationMessages.consentRequired && (
        <div style={{marginTop: 12}}>
          <RequestAdminConsentButton onConsentGranted={() => { setErrorMessage(null); setErrorCode(null); }} />
        </div>
      )}
    </div>
  )
}

interface Props {
}
